import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Loading } from '@carbon/react';
import { Delivery, DeliveryParcel, Money, Store } from '@carbon/react/icons';
import { DashboardDateRange } from '@restavo/shared/enums/dashboard';
import { OrderPaidMethodEnum, OrderTypeEnum } from '@restavo/shared/enums/order';
import DailySales from 'src/components/Dashboard/DailySales';
import ItemsSales from 'src/components/Dashboard/ItemsSales';
import SalesGoal from 'src/components/Dashboard/SalesGoal';
import StatCard from 'src/components/Dashboard/StatCard';
import { useDashboardSalesReports } from 'src/hooks/useDashboardReports';
import { useMoney } from 'src/hooks/useMoney';
import { useBranches, useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { useDashboardStore } from 'src/states/dashboard';

interface SellingProps {}

const Selling = React.memo((_: SellingProps) => {
  const { t } = useTranslation('dashboard');
  const { decimals, formatCompact } = useMoney();
  const navigate = useNavigate();
  const {
    query: { isLoading: salesReportsLoading, data: report, isRefetching },
    bucket,
  } = useDashboardSalesReports();
  const { dateRange, startDate, endDate } = useDashboardStore(
    (state) => ({
      dateRange: state.dateRange,
      startDate: state.startDate,
      endDate: state.endDate,
    }),
    shallow
  );
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const branches = useBranches();
  const totalSales = report?.salesReports?.totalSales || 0;
  const totalTakeawaySales =
    report?.salesReports?.salesReportsByType?.find((item) => item.orderType === OrderTypeEnum.TAKEAWAY)?.totalSales ||
    0;
  const totalDeliverySales =
    report?.salesReports?.salesReportsByType?.find((item) => item.orderType === OrderTypeEnum.DELIVERY)?.totalSales ||
    0;
  const totalCashSales =
    report?.salesReports?.salesReportsByPaidMethod?.find((item) => item.paidMethod === OrderPaidMethodEnum.CASH)
      ?.totalSales || 0;
  const totalBankSales =
    report?.salesReports?.salesReportsByPaidMethod?.find((item) => item.paidMethod === OrderPaidMethodEnum.BANK)
      ?.totalSales || 0;

  const salesByDateRange = report?.salesReports?.salesByDateRange || [];
  const orderItemsSalesReports = report?.orderItemsSalesReports || [];

  const getSalesGoal = useCallback(() => {
    if (!currentRestaurantBranch) {
      switch (dateRange) {
        case DashboardDateRange.DAILY:
          return branches?.data?.data
            ?.map((item) => item.salesGoalPerDay)
            .reduce((total, quantity) => total + quantity);
        case DashboardDateRange.YESTERDAY:
          return branches?.data?.data
            ?.map((item) => item.salesGoalPerDay)
            .reduce((total, quantity) => total + quantity);
        case DashboardDateRange.WEEKLY:
          return branches?.data?.data
            ?.map((item) => item.salesGoalPerWeek)
            .reduce((total, quantity) => total + quantity);
        case DashboardDateRange.MONTHLY:
          return branches?.data?.data
            ?.map((item) => item.salesGoalPerMonth)
            .reduce((total, quantity) => total + quantity);
        case DashboardDateRange.YEARLY:
          return branches?.data?.data
            ?.map((item) => item.salesGoalPerYear)
            .reduce((total, quantity) => total + quantity);
      }
    }
    switch (dateRange) {
      case DashboardDateRange.DAILY:
        return currentRestaurantBranch?.salesGoalPerDay;
      case DashboardDateRange.YESTERDAY:
        return currentRestaurantBranch?.salesGoalPerDay;
      case DashboardDateRange.WEEKLY:
        return currentRestaurantBranch?.salesGoalPerWeek;
      case DashboardDateRange.MONTHLY:
        return currentRestaurantBranch?.salesGoalPerMonth;
      case DashboardDateRange.YEARLY:
        return currentRestaurantBranch?.salesGoalPerYear;
    }
  }, [dateRange, currentRestaurantBranch, branches.data?.data]);

  const handleViewDetails = useCallback(
    (orderType: OrderTypeEnum | null) => {
      const filteredParams = {
        orderType: `${orderType}` || 'all',
        ...(startDate ? { startDate: startDate.toISOString() } : {}),
        endDate: endDate.toISOString(),
      };
      navigate({
        pathname: '/orders',
        search: `?${createSearchParams(filteredParams)}`,
      });
    },
    [startDate, endDate, navigate]
  );

  const isLoading = salesReportsLoading || branches.isLoading;

  return (
    <div className={'selling'}>
      {(isLoading || isRefetching) && <Loading small />}
      {!currentRestaurantBranch && (
        <div className="stats">
          {branches?.data?.data.map((branch) => (
            <StatCard
              key={branch.id}
              title={`${branch.name}`}
              onClick={() => handleViewDetails(null)}
              value={
                report?.salesReports?.totalSalesReportsByBranches.find((sale) => sale.restaurantBranchId === branch.id)
                  ?.totalSales || 0
              }
              format={formatCompact}
              decimals={decimals}
              icon={<Store size={36} />}
            />
          ))}
        </div>
      )}
      <div className="stats">
        <StatCard
          title={t('totalSales')}
          onClick={() => handleViewDetails(null)}
          value={totalSales}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
        <StatCard
          title={t('totalCashSales')}
          onClick={() => handleViewDetails(null)}
          value={totalCashSales}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
        <StatCard
          title={t('totalBankSales')}
          onClick={() => handleViewDetails(null)}
          value={totalBankSales}
          format={formatCompact}
          decimals={decimals}
          icon={<Money size={36} />}
        />
      </div>
      <div className="stats">
        <StatCard
          title={t('totalTakeawaySales')}
          onClick={() => handleViewDetails(OrderTypeEnum.TAKEAWAY)}
          value={totalTakeawaySales}
          format={formatCompact}
          decimals={decimals}
          icon={<DeliveryParcel size={36} />}
        />
        <StatCard
          title={t('totalDeliverySales')}
          onClick={() => handleViewDetails(OrderTypeEnum.DELIVERY)}
          value={totalDeliverySales}
          format={formatCompact}
          decimals={decimals}
          icon={<Delivery size={36} />}
        />
        {report?.foodAppsSalesReports?.map((item) => (
          <StatCard
            key={item.category.id}
            title={item.category.name}
            onClick={() => handleViewDetails(null)}
            value={item.total}
            format={formatCompact}
            decimals={decimals}
            icon={<Money size={36} />}
          />
        ))}
      </div>
      <div className="stats">
        {/* Fed the raw by-type rows, not hand-picked types plus `foodAppsSalesReports`: the goal
            is the branch's `salesGoalPerDay`, which orders are measured against, and the meter's
            "current sales" has to equal the Total Sales tile above it. */}
        <SalesGoal
          goal={getSalesGoal() || 0}
          salesReportsByType={report?.salesReports?.salesReportsByType || []}
          isLoading={isLoading}
          title={t('dailySalesGoal')}
        />
      </div>
      <div className="stats">
        <DailySales
          dateFormat={bucket === 'month' ? 'MM/YYYY' : 'DD/MM'}
          salesByDateRange={salesByDateRange}
          title={bucket === 'month' ? t('salesPerMonth') : t('salesPerDay')}
        />
      </div>
      <div className="stats">
        <ItemsSales
          valueKey={'totalSales'}
          orderItemsSalesReports={orderItemsSalesReports}
          title={t('salesPerMenuItem')}
        />
      </div>
      <div className="stats">
        <ItemsSales
          valueKey={'totalQuantities'}
          orderItemsSalesReports={orderItemsSalesReports}
          title={t('salesPerMenuItemByQuantity')}
        />
      </div>
    </div>
  );
});

export default Selling;
